import type { CSSProperties } from "react";
import clsx from "clsx";
import { formatNumber } from "@marekl/adminer/format";
import styles from "./RankedBars.module.css";

/**
 * Ranked horizontal bars — how much each sender sent, largest first.
 *
 * It replaces the vertical bar chart, which drew one 540px block whenever a
 * single sender carried the traffic: no axis, no second row to compare it to.
 *
 * The markup is a real table, so the chart and its accessible reading are the
 * same thing rather than a canvas with a hidden twin. One measure, one hue:
 * the bars carry magnitude, never identity, so nothing here colours by rank.
 * Every bar is labelled with its value, which is why there is no tooltip
 * repeating it on hover.
 */

export interface RankedBarsDatum {
  label: string;
  value: number;
}

export interface RankedBarsProps {
  data: RankedBarsDatum[];
  /** Column heading for the label column, e.g. "Sender". */
  labelHeader: string;
  /** Column heading for the measure, e.g. "Messages sent". */
  valueHeader: string;
  /** Names what the table shows; visually hidden. */
  caption: string;
  className?: string;
}

/**
 * Round the tick *step* to 1, 2, 2.5 or 5 × a power of ten, then take the axis
 * up to a whole number of them. Rounding the maximum instead put the ticks at
 * quarters of it, which is how an axis ends up labelled 0 · 13 · 25 · 38 · 50.
 */
function niceScale(max: number, wantTicks = 4): { axisMax: number; step: number } {
  if (max <= 0) return { axisMax: 1, step: 1 };
  const rough = max / wantTicks;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const scaled = rough / magnitude;
  const step =
    (scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 2.5 ? 2.5 : scaled <= 5 ? 5 : 10) * magnitude;
  // Messages are counted, never fractioned: a step of 0.25 drew gridlines at
  // 0.25 / 0.5 / 0.75 under labels rounded to 0 / 1 / 1 — the nonsense axis
  // this function exists to prevent, and the shape a quiet instance produces.
  const whole = Math.max(1, Math.round(step));
  return { axisMax: Math.ceil(max / whole) * whole, step: whole };
}

export function RankedBars({
  data,
  labelHeader,
  valueHeader,
  caption,
  className,
}: RankedBarsProps) {
  if (data.length === 0) {
    return <div className={clsx(styles.empty, className)}>Nothing sent in this window</div>;
  }

  const rows = [...data].sort((a, b) => b.value - a.value);
  const { axisMax, step } = niceScale(Math.max(...rows.map((r) => r.value)));
  const ticks = Array.from({ length: Math.round(axisMax / step) + 1 }, (_, i) => i * step);

  return (
    <table className={clsx(styles.chart, className)}>
      <caption className={styles.caption}>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{labelHeader}</th>
          <th scope="col" colSpan={2}>
            {valueHeader}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <th scope="row" className={styles.label}>
              {row.label}
            </th>
            <td className={styles.trackCell}>
              {/* Ticks are drawn as a background grid so bar and axis share
                  one scale by construction. */}
              <div
                className={styles.track}
                // The gridlines are the ticks: one column per step, so the
                // background and the labels below can never drift apart.
                style={{ "--lm-tick": `${100 / (ticks.length - 1)}%` } as CSSProperties}
              >
                <div
                  className={styles.bar}
                  style={{ width: `${(row.value / axisMax) * 100}%` }}
                />
              </div>
            </td>
            <td className={styles.value}>{formatNumber(row.value)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot aria-hidden="true">
        <tr>
          <td />
          <td className={styles.axisCell}>
            <div className={styles.axis}>
              {ticks.map((tick) => (
                <span key={tick}>{formatNumber(Math.round(tick))}</span>
              ))}
            </div>
          </td>
          <td />
        </tr>
      </tfoot>
    </table>
  );
}
